import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Users, IdCard, Flag, Siren, Wallet, MessageSquareWarning, Trash2,
  TrendingUp, Check, X, Ban,
} from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const load = useCallback(() => api.get("/admin/stats").then(({ data }) => setStats(data)), []);
  useEffect(() => { load(); }, [load]);

  const cards = stats ? [
    { icon: Users, label: "Users", value: stats.users, c: "text-charcoal" },
    { icon: TrendingUp, label: "Revenue (₹)", value: stats.revenue, c: "text-sage" },
    { icon: IdCard, label: "KYC pending", value: stats.kyc_pending, c: "text-terracotta" },
    { icon: Flag, label: "Open reports", value: stats.open_reports, c: "text-signal" },
    { icon: Siren, label: "Escalations", value: stats.open_escalations, c: "text-signal" },
    { icon: Wallet, label: "Payouts due", value: stats.pending_payouts, c: "text-charcoal" },
  ] : [];

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-sand">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <h1 className="font-display text-4xl font-black text-charcoal">Admin console</h1>
        <p className="text-charcoal/60 mt-1">Trust & safety, KYC, payouts and compliance.</p>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-6">
          {cards.map((c) => (
            <div key={c.label} className="bg-white rounded-xl border border-border p-4">
              <c.icon className={`w-5 h-5 ${c.c} mb-1`} strokeWidth={1.5} />
              <p className={`font-display text-2xl font-bold ${c.c}`}>{c.value}</p>
              <p className="text-xs text-charcoal/60">{c.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="kyc" className="mt-8">
          <TabsList className="bg-white border border-border rounded-xl p-1 flex-wrap h-auto">
            {[["kyc", "KYC Queue", IdCard], ["reports", "Moderation", Flag], ["escalations", "Safety", Siren],
              ["payouts", "Payouts", Wallet], ["grievances", "Grievances", MessageSquareWarning],
              ["deletions", "Deletions", Trash2], ["users", "Users", Users]].map(([v, l, Icon]) => (
              <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="rounded-lg data-[state=active]:bg-terracotta data-[state=active]:text-white text-sm">
                <Icon className="w-4 h-4 mr-1" /> {l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="kyc"><KYCQueue onChange={load} /></TabsContent>
          <TabsContent value="reports"><Reports onChange={load} /></TabsContent>
          <TabsContent value="escalations"><Escalations onChange={load} /></TabsContent>
          <TabsContent value="payouts"><Payouts onChange={load} /></TabsContent>
          <TabsContent value="grievances"><Grievances onChange={load} /></TabsContent>
          <TabsContent value="deletions"><Deletions onChange={load} /></TabsContent>
          <TabsContent value="users"><UsersTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Panel({ children }) { return <div className="bg-white rounded-2xl border border-border mt-4 overflow-x-auto">{children}</div>; }
function Empty({ t }) { return <p className="text-center text-charcoal/40 py-12 text-sm">{t}</p>; }

function KYCQueue({ onChange }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/kyc-queue").then(({ data }) => setRows(data.queue));
  useEffect(() => { load(); }, []);
  const decide = async (id, d) => { await api.post(`/admin/kyc/${id}/${d}`); toast.success(`KYC ${d}d`); load(); onChange(); };
  return <Panel>{rows.length === 0 ? <Empty t="No pending KYC submissions." /> : (
    <Table><TableHeader><TableRow><TableHead>User</TableHead><TableHead>ID doc</TableHead><TableHead>Selfie</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
      <TableBody>{rows.map((u) => (
        <TableRow key={u.id}><TableCell className="font-semibold">{u.name}<p className="text-xs text-charcoal/50">{u.email}</p></TableCell>
          <TableCell><a href={u.kyc_docs?.id_doc_url} target="_blank" rel="noreferrer" className="text-terracotta underline text-sm">view</a></TableCell>
          <TableCell>{u.kyc_docs?.selfie_url ? <img src={u.kyc_docs.selfie_url} className="w-10 h-10 rounded object-cover" alt="" /> : "—"}</TableCell>
          <TableCell className="text-right space-x-2">
            <Button data-testid={`kyc-approve-${u.id}`} size="sm" onClick={() => decide(u.id, "approve")} className="rounded-full bg-sage hover:bg-sage/90 text-white h-8"><Check className="w-4 h-4" /></Button>
            <Button data-testid={`kyc-reject-${u.id}`} size="sm" variant="ghost" onClick={() => decide(u.id, "reject")} className="rounded-full text-signal h-8"><X className="w-4 h-4" /></Button>
          </TableCell></TableRow>))}
      </TableBody></Table>)}</Panel>;
}

function Reports({ onChange }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/reports").then(({ data }) => setRows(data.reports));
  useEffect(() => { load(); }, []);
  const act = async (id, a) => { await api.post(`/admin/reports/${id}/${a}`); toast.success(`Report ${a}`); load(); onChange(); };
  return <Panel>{rows.length === 0 ? <Empty t="No reports." /> : (
    <Table><TableHeader><TableRow><TableHead>Reporter</TableHead><TableHead>Type</TableHead><TableHead>Reason</TableHead><TableHead>Severity</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
      <TableBody>{rows.map((r) => (
        <TableRow key={r.id}><TableCell className="text-sm">{r.reporter_name}</TableCell><TableCell className="text-sm">{r.target_type}</TableCell>
          <TableCell className="text-sm max-w-xs"><p className="truncate">{r.reason}</p>{r.details && <p className="text-xs text-charcoal/40 truncate">{r.details}</p>}</TableCell>
          <TableCell><Badge className={r.severity === "high" ? "bg-signal text-white" : "bg-sand text-charcoal"}>{r.severity}</Badge></TableCell>
          <TableCell className="text-sm">{r.status}</TableCell>
          <TableCell className="text-right space-x-1">
            {r.status === "open" && <>
              <Button data-testid={`report-ban-${r.id}`} size="sm" onClick={() => act(r.id, "ban")} className="rounded-full bg-signal hover:bg-signal/90 text-white h-8 text-xs"><Ban className="w-3.5 h-3.5 mr-1" />Ban</Button>
              <Button data-testid={`report-remove-${r.id}`} size="sm" variant="outline" onClick={() => act(r.id, "remove")} className="rounded-full h-8 text-xs">Remove</Button>
              <Button data-testid={`report-dismiss-${r.id}`} size="sm" variant="ghost" onClick={() => act(r.id, "dismiss")} className="rounded-full h-8 text-xs">Dismiss</Button>
            </>}
          </TableCell></TableRow>))}
      </TableBody></Table>)}</Panel>;
}

function Escalations({ onChange }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/escalations").then(({ data }) => setRows(data.escalations));
  useEffect(() => { load(); }, []);
  const resolve = async (id) => { await api.post(`/admin/escalations/${id}/resolve`); toast.success("Resolved"); load(); onChange(); };
  return <Panel>{rows.length === 0 ? <Empty t="No safety escalations." /> : (
    <Table><TableHeader><TableRow><TableHead>User</TableHead><TableHead>Type</TableHead><TableHead>Priority</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
      <TableBody>{rows.map((e) => (
        <TableRow key={e.id}><TableCell className="font-semibold">{e.user_name}</TableCell><TableCell><Badge className="bg-signal text-white">{e.type}</Badge></TableCell>
          <TableCell className="text-sm">{e.priority}</TableCell><TableCell className="text-sm">{e.status}</TableCell>
          <TableCell className="text-right">{e.status === "open" && <Button data-testid={`esc-resolve-${e.id}`} size="sm" onClick={() => resolve(e.id)} className="rounded-full bg-sage hover:bg-sage/90 text-white h-8 text-xs">Mark resolved</Button>}</TableCell>
        </TableRow>))}
      </TableBody></Table>)}</Panel>;
}

function Payouts({ onChange }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/payouts").then(({ data }) => setRows(data.payouts));
  useEffect(() => { load(); }, []);
  const pay = async (id) => { await api.post(`/admin/payouts/${id}/pay`); toast.success("Payout marked paid"); load(); onChange(); };
  return <Panel>{rows.length === 0 ? <Empty t="No payouts yet." /> : (
    <Table><TableHeader><TableRow><TableHead>Host</TableHead><TableHead>Amount (₹)</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
      <TableBody>{rows.map((p) => (
        <TableRow key={p.id}><TableCell className="font-semibold">{p.host_name}</TableCell><TableCell>₹{p.amount}</TableCell><TableCell className="text-sm">{p.status}</TableCell>
          <TableCell className="text-right">{p.status === "pending" && <Button data-testid={`pay-${p.id}`} size="sm" onClick={() => pay(p.id)} className="rounded-full bg-sage hover:bg-sage/90 text-white h-8 text-xs">Pay out</Button>}</TableCell>
        </TableRow>))}
      </TableBody></Table>)}</Panel>;
}

function Grievances({ onChange }) {
  const [rows, setRows] = useState([]);
  const [res, setRes] = useState({});
  const load = () => api.get("/admin/grievances").then(({ data }) => setRows(data.grievances));
  useEffect(() => { load(); }, []);
  const resolve = async (id) => { await api.post(`/admin/grievances/${id}/resolve`, null, { params: { resolution: res[id] || "Resolved" } }); toast.success("Grievance resolved"); load(); onChange(); };
  return <Panel>{rows.length === 0 ? <Empty t="No grievances." /> : (
    <Table><TableHeader><TableRow><TableHead>User</TableHead><TableHead>Subject</TableHead><TableHead>SLA due</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Resolve</TableHead></TableRow></TableHeader>
      <TableBody>{rows.map((g) => (
        <TableRow key={g.id}><TableCell className="font-semibold">{g.user_name}</TableCell>
          <TableCell className="text-sm max-w-xs"><p className="font-medium">{g.subject}</p><p className="text-xs text-charcoal/50 truncate">{g.description}</p></TableCell>
          <TableCell className="text-xs">{new Date(g.sla_due).toLocaleDateString("en-IN")}</TableCell>
          <TableCell className="text-sm">{g.status}</TableCell>
          <TableCell className="text-right">{g.status !== "resolved" && <div className="flex gap-1 justify-end">
            <Input value={res[g.id] || ""} onChange={(e) => setRes({ ...res, [g.id]: e.target.value })} placeholder="Resolution" className="h-8 w-32 rounded-full text-xs" />
            <Button data-testid={`griev-resolve-${g.id}`} size="sm" onClick={() => resolve(g.id)} className="rounded-full bg-sage hover:bg-sage/90 text-white h-8 text-xs">Resolve</Button></div>}</TableCell>
        </TableRow>))}
      </TableBody></Table>)}</Panel>;
}

function Deletions({ onChange }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/deletion-requests").then(({ data }) => setRows(data.requests));
  useEffect(() => { load(); }, []);
  const process = async (id) => { await api.post(`/admin/deletion-requests/${id}/process`); toast.success("Processed per retention policy"); load(); onChange(); };
  return <Panel>{rows.length === 0 ? <Empty t="No deletion requests." /> : (
    <Table><TableHeader><TableRow><TableHead>User</TableHead><TableHead>Status</TableHead><TableHead>Requested</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
      <TableBody>{rows.map((d) => (
        <TableRow key={d.id}><TableCell className="font-semibold">{d.user_name}</TableCell><TableCell className="text-sm">{d.status}</TableCell>
          <TableCell className="text-xs">{new Date(d.created_at).toLocaleDateString("en-IN")}</TableCell>
          <TableCell className="text-right">{d.status === "pending" && <Button data-testid={`del-process-${d.id}`} size="sm" onClick={() => process(d.id)} className="rounded-full bg-signal hover:bg-signal/90 text-white h-8 text-xs">Process</Button>}</TableCell>
        </TableRow>))}
      </TableBody></Table>)}</Panel>;
}

function UsersTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/users").then(({ data }) => setRows(data.users)); }, []);
  return <Panel><Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>KYC</TableHead><TableHead>Trust</TableHead><TableHead>Credits</TableHead></TableRow></TableHeader>
    <TableBody>{rows.map((u) => (
      <TableRow key={u.id}><TableCell className="font-semibold">{u.name} {u.is_host && <Badge className="bg-terracotta/10 text-terracotta ml-1">host</Badge>} {u.banned && <Badge className="bg-signal text-white ml-1">banned</Badge>}</TableCell>
        <TableCell className="text-sm text-charcoal/60">{u.email}</TableCell><TableCell className="text-sm">{u.role}</TableCell>
        <TableCell><Badge className={u.kyc_status === "approved" ? "bg-sage text-white" : "bg-sand text-charcoal"}>{u.kyc_status}</Badge></TableCell>
        <TableCell>{u.trust_score}%</TableCell><TableCell>{u.time_credits_minutes || 0}m</TableCell>
      </TableRow>))}
    </TableBody></Table></Panel>;
}
